"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import AnimatedButton from "../ui/AnimatedButton";
import JobSlider from "../slider/jobSlider";
import RevealElement from "../revealElement";
import BottomCurves from "../curves/bottomCurves";

type Jobs = {
    name: string;
    link: string;
};

type Career = {
    department: string;
    jobs: Jobs[];
};

type CareersProps = {
    data: {
        heading: string;
        description: string;
        career: Career[];
        waveBgColor: {
            waveBgColor: string;
        };
    };
};

export default function Careers({ data }: CareersProps) {
    const { heading, description, career: careersTab, waveBgColor } = data;

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState<string>("");
    const [activeTab, setActiveTab] = useState<string>("");

    // Convert department to slug
    const slugify = (str: string) => str.toLowerCase().replace(/\s+/g, "-");

    const deslugify = (slug: string) =>
        slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    // Memoized department slugs
    const departmentSlugs = useMemo(
        () => careersTab.map((c) => slugify(c.department)),
        [careersTab],
    );

    // Initialize tab
    useEffect(() => {
        const queryTab = slugify(
            new URLSearchParams(window.location.search).get("tab") || "",
        );
        const matchedSlug = departmentSlugs.find((slug) => slug === queryTab);

        if (matchedSlug) {
            setActiveTab(matchedSlug);
            setSelectedDepartment(deslugify(matchedSlug));
        } else if (careersTab.length > 0) {
            const defaultSlug = slugify(careersTab[0].department);
            setActiveTab(defaultSlug);
            setSelectedDepartment(careersTab[0].department);
        }
    }, [careersTab, departmentSlugs]);

    const handleTabClick = (slug: string, department: string) => {
        setActiveTab(slug);
        setSelectedDepartment(department);
        setDropdownOpen(false);
        const url = new URL(window.location.href);
        url.searchParams.set("tab", slug);
        window.history.pushState({}, "", url);
    };

    const activeCareer = careersTab.find(
        (c) => slugify(c.department) === activeTab,
    );

    return (
        <section className="bg-rainbow pt-12 lg:pb-[10.833vw] pb-32">
            <RevealElement>
                <h2 className="text-[#263244] text-center">{heading}</h2>
                <p
                    className="text-center careersDescriptionHolder"
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            </RevealElement>

            <div className="careerHolder mb-10 relative z-[1]">
                <RevealElement className="relative z-[100]">
                    {/* Desktop Tabs */}
                    <div className="hidden md:flex flex-wrap gap-y-3 justify-center gap-x-4 mb-8">
                        {careersTab.map((career) => {
                            const slug = slugify(career.department);
                            return (
                                <span
                                    key={slug}
                                    className={`${
                                        activeTab === slug
                                            ? "bg-purple text-white"
                                            : "text-purple bg-transparent"
                                    } flex body-small-fontsize font-medium px-6 py-3 border border-purple rounded-full cursor-pointer hover:bg-purple hover:text-white transition`}
                                    onClick={() =>
                                        handleTabClick(slug, career.department)
                                    }
                                >
                                    {career.department}
                                </span>
                            );
                        })}
                    </div>

                    {/* Mobile Dropdown */}
                    {careersTab.length > 0 && (
                        <div className="relative flex md:hidden flex-col w-full mx-auto mb-8">
                            <span
                                className="flex items-center justify-center body-small-fontsize font-medium text-white bg-purple px-6 py-3 border border-purple rounded-full cursor-pointer"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                {selectedDepartment}
                                <ChevronDownIcon
                                    className={`w-6 h-6 ml-2 transition-transform ${
                                        dropdownOpen ? "rotate-180" : "rotate-0"
                                    }`}
                                />
                            </span>

                            <div
                                className={`absolute flex flex-col mt-[2px] top-full w-full bg-[#E4CEF8] border border-purple rounded-3xl transition-all ${
                                    dropdownOpen
                                        ? "opacity-100 pointer-events-auto"
                                        : "opacity-0 pointer-events-none"
                                }`}
                            >
                                {careersTab.map((career) => {
                                    const slug = slugify(career.department);
                                    return (
                                        <span
                                            key={slug}
                                            className="text-center px-6 py-3 cursor-pointer hover:bg-purple hover:text-white transition"
                                            onClick={() =>
                                                handleTabClick(
                                                    slug,
                                                    career.department,
                                                )
                                            }
                                        >
                                            {career.department}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* No Openings Fallback */}
                    {careersTab.length === 0 && (
                        <p className="text-center careersDescriptionHolder my-0">
                            Although we don’t have any current career openings,
                            we’re always up for a quick chat with talented folks
                            like you! Send us an email and let’s explore how we
                            can create magic together in the future.
                        </p>
                    )}
                </RevealElement>

                {/* Job Slider */}
                <RevealElement className="z-[99]">
                    {activeCareer && (
                        <div className="careerOpenings flex flex-col gap-5 w-full">
                            <JobSlider slides={activeCareer} />
                        </div>
                    )}
                </RevealElement>
            </div>

            {/* CTA */}
            <RevealElement>
                <div className="flex flex-col md:flex-row justify-center items-center gap-4">
                    <p className="text-center font-medium mb-0 text-h4-fontsize">
                        Interested in something else?
                    </p>
                    <AnimatedButton text="Let us know!" link="#kd-form" />
                </div>
            </RevealElement>

            <BottomCurves color={waveBgColor.waveBgColor} direction="right" />
        </section>
    );
}
